import React, { useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FiBriefcase,
  FiFileText,
  FiCalendar,
  FiPlus,
  FiEdit3,
  FiTag,
  FiFolder,
  FiZap,
  FiShield,
  FiArrowRight,
  FiMapPin,
  FiImage,
} from "react-icons/fi";
import SaaSStatCard from "../../common/saas/SaaSStatCard";
import OverviewSection, { HorizontalRow } from "../../customer/dashboard/sections/OverviewSection";
import useAsyncData from "../../customer/dashboard/sections/useAsyncData";
import {
  SafeImage,
  resolveImage,
  formatPrice,
  stripHtml,
  PropertiesNearbySection,
  PostsNearbySection,
  PromotionsSection,
} from "../../customer/dashboard/sections/OverviewSections";
import { pickList, getUserLocation } from "../../../api/customer/overviewApis";
import { getGroupedPartnerLeads, getPartnerSubscriptionDetails } from "../../../api/partner/partnerFeatureApis";
import { getMeetingReminders } from "../../../api/customer/customerFeatureApis";
import { checkPartnerAccess, DOCUMENT_TITLES } from "../../../guards/partnerGuard";
import { partnerTools, INTERIOR_CATALOG_PATH } from "../partnerNavigation";
import { usePartnerService } from "../../../utils/partnerService";
import { loadMyCatalogCount } from "../interior/catalogCount";
import "../../customer/dashboard/CustomerDashboardView.css";
import "../../customer/dashboard/sections/OverviewSections.css";
import "./PartnerDashboardView.css";
import { formatDate as formatDateSafe, toTimestamp } from "../../../utils/date";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const MY_SPACE_PAGE_SIZE = 100;

const authHeaders = (token) => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

const formatDate = (value) => formatDateSafe(value, { fallback: "" });

const capitalize = (text) => (text ? text.charAt(0) + text.slice(1).toLowerCase() : "");

/** Membership badge from the subscription status. Returns null when there is no real status. */
const planBadge = (subscription, answered) => {
  if (!answered) return null;
  const status = String(subscription?.status || "").toUpperCase();
  if (!subscription) return { label: "Not subscribed", tone: "grey" };
  if (status === "ACTIVE") return { label: "Active", tone: "green" };
  if (status === "TRIAL") return { label: "Trial", tone: "blue" };
  if (status === "PENDING") return { label: "Pending", tone: "amber" };
  if (status) return { label: capitalize(status), tone: "red" };
  return null;
};

const Badge = ({ badge }) =>
  badge ? <span className={`pd-badge pd-badge--${badge.tone}`}>{badge.label}</span> : null;

/** Loads the plan and KYC facts once for the two status cards and the Plan stat. */
const loadAccount = async (token, userId) => {
  const [sub, access] = await Promise.allSettled([
    getPartnerSubscriptionDetails(token),
    checkPartnerAccess({ token, userId }),
  ]);
  const subData = sub.status === "fulfilled" ? sub.value : null;
  return {
    subscriptionAnswered: sub.status === "fulfilled",
    subscription: subData?.statusType === "SUCCESS" ? subData.subscription || null : null,
    access: access.status === "fulfilled" ? access.value : null,
  };
};

const getKycBadge = (access) => {
  const user = access?.user;
  if (!access || !user) return null;
  // Same rule the onboarding verification screen uses
  const verified = Boolean(user.active_status || user.is_verified || user.status === "VERIFIED");
  if (verified) return { label: "Verified", tone: "green" };
  if (access.docStatus?.uploadedCount > 0) return { label: "Under review", tone: "amber" };
  return { label: "Not submitted", tone: "grey" };
};

const StatusCard = ({ icon: Icon, title, badge, loading, error, onRetry, rows, text, actionLabel, actionTo }) => (
  <div className="pd-status-card">
    <div className="pd-status-card__head">
      <h3 className="pd-status-card__title">
        <Icon aria-hidden="true" className="text-primary" /> {title}
      </h3>
      <Badge badge={badge} />
    </div>
    {loading ? (
      <div aria-busy="true" className="d-flex flex-column gap-2">
        <div className="ov-skel ov-skel--line" style={{ margin: 0 }} />
        <div className="ov-skel ov-skel--line" style={{ margin: 0, width: "60%" }} />
      </div>
    ) : error ? (
      <div className="ov-state ov-state--error" role="alert">
        <div className="ov-state__text">Couldn't load {title.toLowerCase()}.</div>
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={onRetry}>
          Retry
        </button>
      </div>
    ) : (
      <>
        {text && <p className="pd-status-card__text">{text}</p>}
        {rows.length > 0 && (
          <dl className="pd-status-card__rows">
            {rows.map(({ label, value }) => (
              <div key={label} className="pd-status-card__row">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </>
    )}
    <div className="pd-status-card__foot">
      <Link to={actionTo} className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2">
        <span>{actionLabel}</span>
        <FiArrowRight size={14} aria-hidden="true" />
      </Link>
    </div>
  </div>
);

export const PartnerDashboardView = () => {
  const navigate = useNavigate();
  const { isInteriorDesign } = usePartnerService();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;
  const enabled = Boolean(token);

  const userObj = loggedInUser?.user || loggedInUser?.userDetails || loggedInUser;
  const userId = userObj?.id;
  const rawName =
    [userObj?.first_name || userObj?.firstName, userObj?.last_name || userObj?.lastName].filter(Boolean).join(" ") ||
    userObj?.name ||
    userObj?.customer_name;
  const displayName = rawName && rawName.trim() && !rawName.toLowerCase().includes("null") ? rawName.trim() : "Partner";

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 18 ? "Good Afternoon" : "Good Evening";

  /* ---------------- data ---------------- */
  const account = useAsyncData(() => loadAccount(token, userId), [token, userId], enabled);
  const properties = useAsyncData(
    async () => {
      const res = await axios.post(
        `${BASE_URL}/property/list/my/space`,
        { search_type: "Property", page_number: 1, page_size: MY_SPACE_PAGE_SIZE, order_by: "DESC" },
        { headers: authHeaders(token) }
      );
      return { list: pickList(res.data, ["properties"]), assetUrl: res.data?.assetUrl || "" };
    },
    [token],
    enabled && !isInteriorDesign
  );
  const leads = useAsyncData(
    async () => {
      const data = await getGroupedPartnerLeads(token, { page_number: 1, page_size: 6, order_by: "DESC" });
      const list = data?.groupedPartnerLeads || [];
      return { list, total: data?.totalRecord || list.length || 0 };
    },
    [token],
    enabled && !isInteriorDesign
  );
  const reminders = useAsyncData(
    async () => {
      const data = await getMeetingReminders(token, { pageNumber: 1, pageSize: 5 });
      const list = data?.reminders || data?.data || [];
      return data?.totalRecord || list.length || 0;
    },
    [token],
    enabled && !isInteriorDesign
  );
  // Interior Design partners: real total of their own catalogs (no mock data)
  const catalogCount = useAsyncData(() => loadMyCatalogCount(token, userId), [token, userId], enabled && isInteriorDesign && Boolean(userId));
  // The partner's own promotions (same endpoint the mobile partner profile uses)
  const promotions = useAsyncData(
    async () => {
      const res = await axios.post(
        `${BASE_URL}/promotion/list/my/preferred`,
        { user_id: Number(userId), page_number: 1, page_size: 12, order_by: "DESC" },
        { headers: authHeaders(token) }
      );
      if (res.data?.statusType !== "SUCCESS") throw new Error(res.data?.text || "Failed to load promotions");
      return pickList(res.data, ["promotions"]);
    },
    [token, userId],
    enabled && Boolean(userId)
  );

  // Location for the "nearby" sections (same lookup the Customer Overview and mobile use)
  const locationState = useAsyncData(() => getUserLocation(token, userId), [token, userId], enabled);
  const location = useMemo(() => locationState.data || { state: "", city: "", areas: [] }, [locationState.data]);
  const locationReady = !locationState.loading;

  const statValue = (state, format = (v) => String(v)) =>
    state.error ? "—" : state.loading || state.data === null ? "…" : format(state.data);

  const acc = account.data;
  const subscription = acc?.subscription || null;
  const plan = subscription?.plan || null;
  const planState = planBadge(subscription, Boolean(acc?.subscriptionAnswered));
  const kycBadge = getKycBadge(acc?.access);
  const isVerified = kycBadge?.label === "Verified";

  const propertyList = properties.data?.list || [];
  const propertyCount = propertyList.length >= MY_SPACE_PAGE_SIZE ? `${MY_SPACE_PAGE_SIZE}+` : String(propertyList.length);
  const recentLeads = leads.data?.list || [];
  const promoList = promotions.data || [];

  /* ---------------- plan & KYC card rows (only real values) ---------------- */
  const subscriptionEnd = toTimestamp(subscription?.end_date);
  const daysLeft = subscriptionEnd !== null ? Math.ceil((subscriptionEnd - Date.now()) / 86400000) : null;
  const planRows = [
    plan?.plan_name && { label: "Plan Tier", value: plan.plan_name },
    formatDate(subscription?.end_date) && { label: "Valid Till", value: formatDate(subscription.end_date) },
    daysLeft !== null && daysLeft >= 0 && { label: "Days Remaining", value: `${daysLeft} days` },
  ].filter(Boolean);

  const docStatus = acc?.access?.docStatus;
  const uploadedTitles = docStatus
    ? Object.keys(docStatus.uploadedDocsMap || {}).map((key) => DOCUMENT_TITLES[key] || key)
    : [];
  const kycRows = [
    docStatus?.requiredDocs?.length > 0 && {
      label: "Mandatory Documents",
      value: `${docStatus.requiredDocs.length - docStatus.missingDocs.length} of ${docStatus.requiredDocs.length} uploaded`,
    },
    uploadedTitles.length > 0 && { label: "Uploaded", value: uploadedTitles.join(", ") },
  ].filter(Boolean);

  return (
    <div className="ov-page">
      {/* 1. Header card */}
      <div className="customer-dash-welcome">
        <div className="customer-dash-welcome__left">
          <div className="customer-dash-welcome__badge">
            <FiShield size={13} aria-hidden="true" /> {isVerified ? "Verified Partner" : "Partner workspace"}
          </div>
          <h1 className="customer-dash-welcome__title">
            {greeting}, {displayName}!
          </h1>
          <p className="customer-dash-welcome__subtitle">
            {isInteriorDesign
              ? "Showcase your interior design catalogs, share your work and grow your business."
              : "Manage your property listings, track client leads, monitor appointments and grow your business."}
          </p>
        </div>
        <div className="customer-dash-welcome__actions pd-actions">
          {isInteriorDesign ? (
            <>
              <button type="button" className="btn btn-primary btn-sm px-3 py-2 rounded-3 fw-medium shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate(`${INTERIOR_CATALOG_PATH}?add=1`)}>
                <FiPlus size={15} aria-hidden="true" />
                <span>Add Catalog</span>
              </button>
              <button type="button" className="btn btn-outline-primary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate(INTERIOR_CATALOG_PATH)}>
                <FiImage size={15} aria-hidden="true" />
                <span>My Catalogs</span>
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary btn-sm px-3 py-2 rounded-3 fw-medium shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/property/add-property")}>
              <FiPlus size={15} aria-hidden="true" />
              <span>Add Listing</span>
            </button>
          )}
          <button type="button" className="btn btn-outline-primary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/post/add-post")}>
            <FiEdit3 size={15} aria-hidden="true" />
            <span>Create Post</span>
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/partner/my-promotions/new")}>
            <FiTag size={15} aria-hidden="true" />
            <span>Create Promotion</span>
          </button>
          {!isInteriorDesign && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/partner/my-properties")}>
                <FiFolder size={15} className="text-secondary" aria-hidden="true" />
                <span>MySpace</span>
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/partner/properties")}>
                <FiBriefcase size={15} className="text-secondary" aria-hidden="true" />
                <span>Browse Properties</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* 2. Stat cards */}
      <div className="ov-stats">
        {isInteriorDesign && (
          <SaaSStatCard
            title="Total Catalogs"
            value={statValue(catalogCount)}
            icon={<FiImage />}
            color="blue"
            subtitle="Your design portfolio"
            onClick={() => navigate(INTERIOR_CATALOG_PATH)}
          />
        )}
        {!isInteriorDesign && (
          <>
        <SaaSStatCard
          title="Active Listings"
          value={statValue(properties, () => propertyCount)}
          icon={<FiBriefcase />}
          color="blue"
          subtitle="Marketplace properties"
          onClick={() => navigate("/partner/my-properties")}
        />
        <SaaSStatCard
          title="Customer Leads"
          value={statValue(leads, (d) => String(d.total))}
          icon={<FiFileText />}
          color="green"
          subtitle="High-intent inquiries"
          onClick={() => navigate("/partner/leads")}
        />
          </>
        )}
        <SaaSStatCard
          title="Partner Plan"
          value={account.error ? "—" : account.loading || !acc ? "…" : planState?.label || "—"}
          icon={<FiZap />}
          color="amber"
          subtitle={plan?.plan_name || "Membership status"}
          onClick={() => navigate("/partner/subscription")}
        />
        {!isInteriorDesign && (
          <SaaSStatCard
            title="Site Reminders"
            value={statValue(reminders)}
            icon={<FiCalendar />}
            color="purple"
            subtitle="Scheduled client visits"
            onClick={() => navigate("/partner/reminders")}
          />
        )}
      </div>

      {/* 3. Partner Tools (Partner only; tiles come from the Partner config) */}
      <OverviewSection
        id="partner-tools"
        title="Partner Tools"
        description="Keep your cash and projects in order."
        isEmpty={false}
      >
        <div className="partner-tools-tiles">
          {partnerTools.map(({ to, label, description, icon: Icon }, i) => (
            <Link key={to} to={to} className={`partner-tools-tile partner-tools-tile--${i % 3}`}>
              <span className="partner-tools-tile__icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <span className="partner-tools-tile__body">
                <span className="partner-tools-tile__title">{label}</span>
                <span className="partner-tools-tile__desc">{description}</span>
              </span>
              <FiArrowRight className="partner-tools-tile__arrow" size={16} aria-hidden="true" />
            </Link>
          ))}
        </div>
      </OverviewSection>

      {/* 4. Plan and KYC */}
      <div className="pd-status-grid">
        <StatusCard
          icon={FiZap}
          title="Membership & Current Plan"
          badge={planState}
          loading={account.loading || (!acc && !account.error)}
          error={account.error}
          onRetry={account.retry}
          rows={planRows}
          text={planRows.length === 0 ? "No plan details are available for your account yet." : undefined}
          actionLabel="Manage Plan & Upgrades"
          actionTo="/partner/subscription"
        />
        <StatusCard
          icon={FiShield}
          title="KYC & Document Compliance"
          badge={kycBadge}
          loading={account.loading || (!acc && !account.error)}
          error={account.error}
          onRetry={account.retry}
          rows={kycRows}
          text="Compliance documents keep your partner profile trusted and eligible for customer leads."
          actionLabel="View Documents & Profile"
          actionTo="/partner/profile"
        />
      </div>

      {/* 7. Recent inquiries (property leads): not for Interior Design partners */}
      {!isInteriorDesign && (
      <OverviewSection
        id="recent-leads"
        title="Recent Customer Inquiries & Leads"
        description="Verified inquiries from buyers and tenants interested in your properties."
        viewAllTo="/partner/leads"
        viewAllLabel="View All Leads"
        loading={leads.loading}
        error={leads.error}
        onRetry={leads.retry}
        isEmpty={recentLeads.length === 0}
      >
        <HorizontalRow label="Recent inquiries">
          {recentLeads.map((lead, idx) => {
            const customerCount = lead.customer_count || lead.customer_ids?.length || 1;
            const place = [lead.area, lead.city, lead.state].filter(Boolean).join(", ");
            return (
              <article key={lead.id ?? idx} className="ov-card" role="listitem">
                <Link to="/partner/leads" className="ov-card__link" aria-label={`View inquiry ${lead.lead_name || ""}`}>
                  <div className="ov-card__body">
                    <h3 className="ov-card__title" title={lead.lead_name}>
                      {lead.lead_name || "Property inquiry"}
                    </h3>
                    <div className="ov-card__chips">
                      <span className="ov-chip">
                        {customerCount} {customerCount === 1 ? "customer" : "customers"}
                      </span>
                    </div>
                    {place && (
                      <p className="ov-card__meta">
                        <FiMapPin size={13} aria-hidden="true" /> <span>{place}</span>
                      </p>
                    )}
                    <div className="ov-card__foot">
                      <span className="ov-card__cta">
                        Open leads <FiArrowRight size={13} aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            );
          })}
        </HorizontalRow>
      </OverviewSection>
      )}

      {/* 8. My promotions */}
      <OverviewSection
        id="my-promotions"
        title="Active Promotions & Campaigns"
        description="Your campaigns, shown to customers across the marketplace."
        viewAllTo="/partner/my-promotions"
        viewAllLabel="Manage Promotions"
        loading={promotions.loading}
        error={promotions.error}
        onRetry={promotions.retry}
        isEmpty={promoList.length === 0}
      >
        <HorizontalRow label="My promotions">
          {promoList.map((promo) => {
            const image = resolveImage(promo.propertyImages?.[0]?.image_url || promo.images?.[0]?.image_url);
            const place = [promo.city_name, promo.state_name].filter(Boolean).join(", ");
            const description = stripHtml(promo.project_description);
            return (
              <article key={promo.id} className="ov-card" role="listitem">
                <Link
                  to={`/partner/my-promotions/${promo.id}`}
                  className="ov-card__link"
                  aria-label={`View ${promo.project_name || "promotion"}`}
                >
                  <div className="ov-media-wrap">
                    <SafeImage src={image} alt={promo.project_name || "Promotion"} />
                    <span className="ov-badge">
                      <FiTag size={12} aria-hidden="true" /> Active Campaign
                    </span>
                  </div>
                  <div className="ov-card__body">
                    <h3 className="ov-card__title" title={promo.project_name}>
                      {promo.project_name || "Promotion"}
                    </h3>
                    {description && <p className="pd-card-desc">{description}</p>}
                    {place && (
                      <p className="ov-card__meta">
                        <FiMapPin size={13} aria-hidden="true" /> <span>{place}</span>
                      </p>
                    )}
                    {promo.starting_cost ? <p className="ov-card__price">From {formatPrice(promo.starting_cost)}</p> : null}
                    <div className="ov-card__foot">
                      <span className="ov-card__cta">
                        Details <FiArrowRight size={13} aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            );
          })}
        </HorizontalRow>
      </OverviewSection>

      {/* 9. Nearby and shared sections; each one removes itself when it has no data */}
      {!isInteriorDesign && (
        <PropertiesNearbySection token={token} location={location} locationReady={locationReady} basePath="/partner" />
      )}
      <PostsNearbySection token={token} location={location} locationReady={locationReady} basePath="/partner" />
      <PromotionsSection
        token={token}
        basePath="/partner"
        title="Shared Promotions"
        description="Featured projects and offers shared by other partners."
      />
    </div>
  );
};

export default PartnerDashboardView;
